<template>
  <div :class="[$style.recommend_product_list, bgColor]">
    <div :class="$style.product_list_wrapper">
      <!-- 価格別おすすめアイテム -->
      <p>価格で探す</p>
      <h2>PRICE</h2>
      <div :class="$style.navigation_list">
        <AtomsConsumerButton v-if="filteredProductsLessThan10000.length > 0" @click="scrollToId('price-1')">
          # 1万円以内
          <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="down" />
        </AtomsConsumerButton>
        <AtomsConsumerButton v-if="filteredProductsBetween10000And30000.length > 0" @click="scrollToId('price-2')">
          # 1～3万円台
          <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="down" />
        </AtomsConsumerButton>
        <AtomsConsumerButton v-if="filteredProductsGreaterThan30000.length > 0" @click="scrollToId('price-3')">
          # 3万円以上
          <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="down" />
        </AtomsConsumerButton>
      </div>
      <section v-if="filteredProductsLessThan10000.length > 0" id="price-1" :class="$style.product_list_section">
        <h3>1万円以内のおすすめアイテム</h3>
        <div :class="$style.product_list">
          <div v-for="product in filteredProductsLessThan10000" :key="`price-1-${product.document_id}`">
            <PagePartsProductListItem
              :key="product.document_id"
              :product="product"
              is-no-validation
              @open-fav-modal="openFavModal($event, product)"
            />
          </div>
        </div>
      </section>
      <section v-if="filteredProductsBetween10000And30000.length > 0" id="price-2" :class="$style.product_list_section">
        <h3>1～3万円のおすすめアイテム</h3>
        <div :class="$style.product_list">
          <div v-for="product in filteredProductsBetween10000And30000" :key="`price-2-${product.document_id}`">
            <PagePartsProductListItem
              :key="product.document_id"
              :product="product"
              is-no-validation
              @open-fav-modal="openFavModal($event, product)"
            />
          </div>
        </div>
      </section>
      <section v-if="filteredProductsGreaterThan30000.length > 0" id="price-3" :class="$style.product_list_section">
        <h3>3万円以上のおすすめアイテム</h3>
        <div :class="$style.product_list">
          <div v-for="product in filteredProductsGreaterThan30000" :key="`price-3-${product.document_id}`">
            <PagePartsProductListItem
              :key="product.document_id"
              :product="product"
              is-no-validation
              @open-fav-modal="openFavModal($event, product)"
            />
          </div>
        </div>
      </section>
    </div>
    <!-- お気に入りモーダル -->
    <MoleculesConsumerModal v-model="isOpenFavModal">
      <ProductSelectFav
        :product="favModalProduct"
        :favorite-list="favoriteListRef"
        :is-processing="isProcessing"
        @on-favorite="onFavoriteByProductList"
        @lift="() => (isOpenFavModal = false)"
      />
    </MoleculesConsumerModal>
  </div>
</template>

<script setup lang="ts">
import { calcPrice } from "@tential/ec-gql-schema/utils/functions/price"
import { PagePartsFragmentFragment } from "~/types/pageParts"
import { PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery } from "~/types/type.generated"
import { usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery } from "~/gql/urql.generated"
import { PagePartsProductType } from "~/types/product"
import { useFavorite } from "~/composables/useFavorite"

const { openFavModal, onFavoriteByProductList, isOpenFavModal, favModalProduct, favoriteListRef, isProcessing } =
  useFavorite()

type Props = {
  products: PagePartsProductType[]
  listParts: PagePartsFragmentFragment["list"]
  bgColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  products: () => [],
  listParts: () => [],
  bgColor: "",
})

type State = {
  products: PagePartsProductType[]
  isDisplayCartModal: boolean
  cartModalProduct: PagePartsProductType | null
}
const state: State = reactive({
  products: [],
  isDisplayCartModal: false,
  cartModalProduct: null,
})

const deliveryLtPrefectures = ref<
  PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery["publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures"]
>({
  default_prefecture: "",
  prefectures: [],
})
const { data: _calcDeliveryLtPrefecturesData, executeQuery: _calcDeliveryLtPrefecturesExecuteQuery } =
  usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery({
    requestPolicy: "cache-first",
    pause: true,
  })

const getPriceWithTax = (product: PagePartsProductType) => {
  return calcPrice(product?.skus[0]).effective_price
}

const filteredProductsLessThan10000 = computed(() => {
  return state.products.filter((product) => getPriceWithTax(product) < 10000)
})

const filteredProductsBetween10000And30000 = computed(() => {
  return state.products.filter((product) => getPriceWithTax(product) >= 10000 && getPriceWithTax(product) <= 30000)
})

const filteredProductsGreaterThan30000 = computed(() => {
  return state.products.filter((product) => getPriceWithTax(product) > 30000)
})

const scrollToId = (id: string): void => {
  const element = document.getElementById(id)
  element?.scrollIntoView({
    behavior: "smooth",
  })
}

onMounted(async () => {
  if (_calcDeliveryLtPrefecturesExecuteQuery().isPaused) _calcDeliveryLtPrefecturesExecuteQuery().resume()
  await _calcDeliveryLtPrefecturesExecuteQuery()
  deliveryLtPrefectures.value = _calcDeliveryLtPrefecturesData.value
    ?.publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures ?? {
    default_prefecture: "",
    prefectures: [],
  }

  if (props.products) {
    state.products = props.products.map((product) => {
      const _product = product
      _product.url = ""
      return _product
    })
  }
})
</script>

<style scoped module lang="scss">
@import "assets/scss/components/RecommendProductListStyle.scss";
</style>
